import { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion, AnimatePresence } from "framer-motion";
import { ArrowRight, Gamepad2, ShieldCheck, MapPin } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import GroveLaunchWaitlistModal from "@/components/home/GroveLaunchWaitlistModal";
import GroveLaunchTermsModal from "@/components/home/GroveLaunchTermsModal";

import cover from "@/assets/gta6/official_cover_art.jpg.asset.json";
import jl1 from "@/assets/gta6/jason_and_lucia_01.jpg.asset.json";
import jl2 from "@/assets/gta6/jason_and_lucia_02.jpg.asset.json";
import jl3 from "@/assets/gta6/jason_and_lucia_03.jpg.asset.json";
import motel from "@/assets/gta6/jason_and_lucia_motel.jpg.asset.json";
import viceCity from "@/assets/gta6/vice_city_postcard.jpg.asset.json";
import leonida from "@/assets/gta6/leonida_keys_postcard.jpg.asset.json";

const TARGET_TS = new Date("2026-06-25T00:00:00+05:30").getTime();
const MAX_SPOTS = 50;

const SLIDES = [
  { src: jl1.url, alt: "Jason and Lucia — GTA VI key art" },
  { src: motel.url, alt: "Jason and Lucia at the Vice City motel" },
  { src: jl2.url, alt: "Jason and Lucia getaway — GTA VI" },
  { src: jl3.url, alt: "Jason and Lucia neon night — GTA VI" },
  { src: cover.url, alt: "GTA VI official cover art" },
];

// GTA VI palette (hero-scoped only)
const PINK = "#FF3D8A";
const CYAN = "#19E0FF";
const NIGHT = "#0A0420";

function useCountdown(target: number) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target - now);
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    mins: Math.floor((diff % 3600000) / 60000),
    secs: Math.floor((diff % 60000) / 1000),
  };
}
const pad = (n: number) => String(n).padStart(2, "0");

const HeroSection = () => {
  const [mouse, setMouse] = useState({ x: 50, y: 50 });
  const [openWaitlist, setOpenWaitlist] = useState(false);
  const [openTerms, setOpenTerms] = useState(false);
  const [slideIdx, setSlideIdx] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const reduceMotion = useReducedMotion();
  const { days, hours, mins, secs } = useCountdown(TARGET_TS);

  useEffect(() => {
    if (reduceMotion) return;
    const id = setInterval(() => setSlideIdx((i) => (i + 1) % SLIDES.length), 7000);
    return () => clearInterval(id);
  }, [reduceMotion]);

  const { data: claimed = 0 } = useQuery({
    queryKey: ["grove-launch-count"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("grove_launch_count");
      if (error) throw error;
      return Number(data) || 0;
    },
    refetchOnWindowFocus: true,
    refetchInterval: 30000,
  });
  const remaining = Math.max(0, MAX_SPOTS - claimed);
  const pct = Math.min(100, Math.round((claimed / MAX_SPOTS) * 100));
  const isFull = claimed >= MAX_SPOTS;

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const handleMouse = (e: React.MouseEvent) => {
    if (reduceMotion) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setMouse({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  const marqueeText =
    "★ VICE CITY DROP  •  GTA VI LAUNCH DAY — 25 . 06 . 2026  •  20% OFF UP TO ₹1,200 PSN CREDIT  •  FIRST 50 BUYERS  •  INDIA EXCLUSIVE  •  ";

  const tiles = [
    { val: pad(days), label: "DAYS" },
    { val: pad(hours), label: "HRS" },
    { val: pad(mins), label: "MIN" },
    { val: pad(secs), label: "SEC", glitch: true },
  ];

  return (
    <>
      <section
        ref={sectionRef}
        onMouseMove={handleMouse}
        className="relative w-full min-h-screen overflow-hidden"
        style={{ background: NIGHT }}
      >
        {/* Slideshow */}
        <motion.div style={{ y: bgY }} className="absolute inset-0 will-change-transform">
          <AnimatePresence mode="sync">
            <motion.img
              key={slideIdx}
              src={SLIDES[slideIdx].src}
              alt={SLIDES[slideIdx].alt}
              initial={{ opacity: 0, scale: 1.08 }}
              animate={{ opacity: 1, scale: 1.15 }}
              exit={{ opacity: 0 }}
              transition={{ opacity: { duration: 1.4 }, scale: { duration: 9, ease: "linear" } }}
              className="absolute inset-0 h-full w-full object-cover"
              width={1920}
              height={1080}
              loading={slideIdx === 0 ? "eager" : "lazy"}
              // @ts-expect-error fetchpriority is valid HTML
              fetchpriority={slideIdx === 0 ? "high" : "auto"}
              decoding="async"
            />
          </AnimatePresence>
        </motion.div>

        {/* Sunset gradient + readability */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: `linear-gradient(180deg, rgba(10,4,32,0.35) 0%, rgba(10,4,32,0.05) 35%, rgba(10,4,32,0.65) 70%, ${NIGHT} 100%)`,
          }}
        />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: `linear-gradient(90deg, ${NIGHT}f0 0%, ${NIGHT}80 35%, transparent 70%)`,
          }}
        />
        {/* Pink → cyan neon wash */}
        <div
          className="pointer-events-none absolute inset-0 mix-blend-screen opacity-40"
          style={{
            background: `radial-gradient(ellipse at 15% 100%, ${PINK}55 0%, transparent 50%), radial-gradient(ellipse at 90% 0%, ${CYAN}44 0%, transparent 55%)`,
          }}
        />
        {/* Mouse glow */}
        <div
          className="pointer-events-none absolute inset-0 transition-[background] duration-300"
          style={{
            background: `radial-gradient(circle 520px at ${mouse.x}% ${mouse.y}%, ${PINK}33 0%, transparent 60%)`,
          }}
        />
        {/* Scanlines */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.12] mix-blend-overlay"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, rgba(255,255,255,0.6) 0 1px, transparent 1px 3px)",
          }}
        />
        {/* Grain */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.7'/></svg>\")",
          }}
        />

        {/* Palm silhouettes */}
        <svg
          className="pointer-events-none absolute bottom-0 left-0 h-[55%] w-auto opacity-70"
          viewBox="0 0 200 400"
          fill={NIGHT}
          aria-hidden
        >
          <path d="M40 400 L55 180 Q40 120 5 90 Q45 110 60 150 Q55 80 30 30 Q70 70 75 140 Q90 70 140 40 Q100 90 90 160 Q140 110 195 110 Q140 140 95 190 L70 400 Z" />
        </svg>
        <svg
          className="pointer-events-none absolute bottom-0 right-0 h-[45%] w-auto opacity-60 -scale-x-100"
          viewBox="0 0 200 400"
          fill={NIGHT}
          aria-hidden
        >
          <path d="M40 400 L55 180 Q40 120 5 90 Q45 110 60 150 Q55 80 30 30 Q70 70 75 140 Q90 70 140 40 Q100 90 90 160 Q140 110 195 110 Q140 140 95 190 L70 400 Z" />
        </svg>

        <motion.div
          style={{ y: contentY, opacity: contentOpacity }}
          className="relative z-10 mx-auto flex min-h-screen max-w-7xl items-center px-5 py-24 lg:px-8"
        >
          <div className="grid w-full gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16 items-center">
            {/* LEFT */}
            <div>
              <motion.span
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 rounded-sm border px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.25em] backdrop-blur-sm"
                style={{ borderColor: `${PINK}80`, background: `${PINK}1a`, color: PINK }}
              >
                <MapPin className="h-3 w-3" />
                Vice City Drop · India Exclusive
              </motion.span>

              <h1
                className="mt-5 font-display font-black italic leading-[0.88] tracking-tight"
                style={{ fontSize: "clamp(3rem, 9vw, 7rem)" }}
              >
                <span
                  className="block bg-clip-text text-transparent drop-shadow-[0_4px_24px_rgba(255,61,138,0.35)]"
                  style={{
                    backgroundImage: `linear-gradient(95deg, ${PINK} 0%, #ffb3d1 40%, ${CYAN} 100%)`,
                    WebkitTextStroke: "1px rgba(255,255,255,0.08)",
                  }}
                >
                  GROVE
                </span>
                <span
                  className="block text-[0.85em] bg-clip-text text-transparent"
                  style={{
                    backgroundImage: `linear-gradient(95deg, ${CYAN} 0%, #b3f6ff 50%, ${PINK} 100%)`,
                  }}
                >
                  × GTA VI
                </span>
              </h1>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.35 }}
                className="mt-6 max-w-xl text-lg leading-relaxed text-white/90 sm:text-xl"
              >
                Cop <span className="font-bold" style={{ color: PINK }}>GTA VI</span> on launch day → cash back{" "}
                <span className="font-bold" style={{ color: CYAN }}>20% up to ₹1,200</span> straight to your PSN Wallet.
              </motion.p>
              <p className="mt-2 text-sm font-semibold uppercase tracking-widest" style={{ color: `${CYAN}cc` }}>
                First 50 verified buyers · Welcome to Leonida.
              </p>

              {/* Progress */}
              <div className="mt-7 max-w-md">
                <div className="mb-2 flex items-center justify-between text-[11px] font-black uppercase tracking-[0.2em]">
                  <span style={{ color: PINK }}>Spots Claimed</span>
                  <span className="tabular-nums text-white">
                    {claimed} / {MAX_SPOTS}
                    {!isFull && <span className="ml-2" style={{ color: CYAN }}>({remaining} left)</span>}
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full" style={{ background: "#1a0b2e" }}>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                    className="h-full rounded-full"
                    style={{
                      background: `linear-gradient(90deg, ${PINK}, ${CYAN})`,
                      boxShadow: `0 0 18px ${PINK}cc`,
                    }}
                  />
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55 }}
                className="mt-8 flex flex-col gap-3 sm:flex-row"
              >
                <button
                  onClick={() => setOpenWaitlist(true)}
                  disabled={isFull}
                  className="group inline-flex items-center justify-center gap-2 rounded-sm px-8 py-4 text-sm font-black uppercase tracking-widest text-white transition-all hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
                  style={{
                    background: `linear-gradient(135deg, ${PINK}, #ff66a8)`,
                    boxShadow: `0 0 36px ${PINK}aa, 0 0 80px ${PINK}55, inset 0 0 0 1px rgba(255,255,255,0.18)`,
                  }}
                >
                  <Gamepad2 className="h-4 w-4" />
                  {isFull ? "Waitlist Full" : "Join the Heist"}
                  {!isFull && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />}
                </button>
                <button
                  onClick={() => setOpenTerms(true)}
                  className="inline-flex items-center justify-center gap-2 rounded-sm border-2 bg-transparent px-8 py-4 text-sm font-black uppercase tracking-widest backdrop-blur-sm transition-all hover:bg-white/5"
                  style={{ borderColor: CYAN, color: CYAN, boxShadow: `0 0 18px ${CYAN}55` }}
                >
                  <ShieldCheck className="h-4 w-4" />
                  Read the Rules
                </button>
              </motion.div>

              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs font-semibold uppercase tracking-widest text-white/70">
                <span>🇮🇳 India only</span>
                <span style={{ color: `${CYAN}cc` }}>🎮 PS5 / PS Store</span>
                <span style={{ color: `${PINK}cc` }}>⚡ Credit within 48 hrs</span>
              </div>
            </div>

            {/* RIGHT — Wanted Poster countdown */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.7 }}
              className="hidden lg:block"
            >
              <div
                className="relative rounded-md border-2 p-6 backdrop-blur-xl"
                style={{
                  borderColor: PINK,
                  background: `linear-gradient(160deg, ${NIGHT}f5, #1a0b2ef5)`,
                  boxShadow: `0 0 60px ${PINK}55, inset 0 0 60px rgba(25,224,255,0.06)`,
                }}
              >
                {/* corner brackets */}
                {(["tl","tr","bl","br"] as const).map((c) => (
                  <span
                    key={c}
                    className={`absolute h-4 w-4 border-[${CYAN}] ${
                      c === "tl" ? "left-2 top-2 border-l-2 border-t-2" :
                      c === "tr" ? "right-2 top-2 border-r-2 border-t-2" :
                      c === "bl" ? "left-2 bottom-2 border-l-2 border-b-2" :
                      "right-2 bottom-2 border-r-2 border-b-2"
                    }`}
                    style={{ borderColor: CYAN }}
                  />
                ))}

                <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-[0.25em]">
                  <span style={{ color: CYAN }}>◉ Launch In</span>
                  <span className="rounded-sm px-2 py-0.5" style={{ background: `${PINK}22`, color: PINK }}>
                    25 . 06 . 2026 · IST
                  </span>
                </div>

                <h3
                  className="mt-3 font-display text-3xl font-black italic leading-none"
                  style={{
                    backgroundImage: `linear-gradient(95deg, ${PINK}, ${CYAN})`,
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  GTA VI<br />drops in
                </h3>

                <div className="mt-6 grid grid-cols-4 gap-3">
                  {tiles.map((t) => (
                    <div key={t.label} className="flex flex-col items-center">
                      <span
                        className={`flex h-16 w-full items-center justify-center rounded-sm border font-display text-2xl font-black tabular-nums ${t.glitch ? "animate-pulse" : ""}`}
                        style={{
                          borderColor: `${CYAN}88`,
                          background: NIGHT,
                          color: CYAN,
                          textShadow: `0 0 12px ${CYAN}, 0 0 24px ${CYAN}66`,
                          boxShadow: `inset 0 0 18px ${PINK}33, 0 0 14px ${CYAN}33`,
                        }}
                      >
                        {t.val}
                      </span>
                      <span
                        className="mt-1.5 text-[10px] font-black tracking-[0.25em]"
                        style={{ color: PINK }}
                      >
                        {t.label}
                      </span>
                    </div>
                  ))}
                </div>

                <div
                  className="mt-6 rounded-sm border p-3 text-[11px] text-white/80"
                  style={{ borderColor: `${PINK}55`, background: "#0d0420aa" }}
                >
                  <div className="flex justify-between">
                    <span className="uppercase tracking-widest" style={{ color: CYAN }}>Reward</span>
                    <span className="font-bold text-white">20% · up to ₹1,200</span>
                  </div>
                  <div className="mt-1 flex justify-between">
                    <span className="uppercase tracking-widest" style={{ color: CYAN }}>Format</span>
                    <span className="font-bold text-white">PSN Wallet Credit</span>
                  </div>
                  <div className="mt-1 flex justify-between">
                    <span className="uppercase tracking-widest" style={{ color: CYAN }}>Window</span>
                    <span className="font-bold text-white">First 50 verified</span>
                  </div>
                </div>

                <button
                  onClick={() => setOpenWaitlist(true)}
                  disabled={isFull}
                  className="mt-5 flex w-full items-center justify-center gap-2 rounded-sm py-3 text-xs font-black uppercase tracking-[0.25em] text-white transition-all hover:opacity-90 disabled:opacity-60"
                  style={{
                    background: `linear-gradient(90deg, ${PINK}, ${CYAN})`,
                    boxShadow: `0 0 24px ${PINK}88`,
                  }}
                >
                  {isFull ? "Waitlist Full" : "Claim My Spot"} <ArrowRight className="h-3 w-3" />
                </button>

                {/* Postcards */}
                <div className="mt-6 flex gap-3">
                  {[
                    { src: viceCity.url, label: "Vice City", rot: -4 },
                    { src: leonida.url, label: "Leonida Keys", rot: 3 },
                  ].map((p) => (
                    <div
                      key={p.label}
                      className="relative flex-1 overflow-hidden rounded-sm border-2 bg-white p-1"
                      style={{
                        borderColor: "#fff",
                        transform: `rotate(${p.rot}deg)`,
                        boxShadow: `0 8px 20px rgba(0,0,0,0.5), 0 0 0 1px ${PINK}55`,
                      }}
                    >
                      <img src={p.src} alt={`${p.label} postcard`} className="h-20 w-full object-cover" loading="lazy" />
                      <div
                        className="absolute bottom-1 left-1 right-1 rounded-sm px-1.5 py-0.5 text-center font-display text-[10px] font-black italic tracking-wider"
                        style={{ background: "rgba(255,255,255,0.92)", color: PINK }}
                      >
                        {p.label.toUpperCase()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* MOBILE countdown */}
            <div className="flex flex-col items-center gap-2 lg:hidden">
              <span className="text-xs font-black uppercase tracking-[0.25em]" style={{ color: PINK }}>
                ◉ Launch In
              </span>
              <div className="grid grid-cols-4 gap-2">
                {tiles.map((t) => (
                  <div key={t.label} className="flex flex-col items-center">
                    <span
                      className="flex h-12 w-14 items-center justify-center rounded-sm border font-display text-xl font-black tabular-nums"
                      style={{
                        borderColor: `${CYAN}88`,
                        background: NIGHT,
                        color: CYAN,
                        textShadow: `0 0 10px ${CYAN}`,
                      }}
                    >
                      {t.val}
                    </span>
                    <span className="mt-1 text-[9px] font-black tracking-[0.25em]" style={{ color: PINK }}>
                      {t.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Marquee */}
      <div
        className="relative w-full overflow-hidden py-3"
        style={{
          background: `linear-gradient(90deg, ${NIGHT}, #1a0b2e, ${NIGHT})`,
          borderTop: `1px solid ${PINK}66`,
          borderBottom: `1px solid ${CYAN}66`,
        }}
      >
        <div className="marquee-track flex whitespace-nowrap">
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              className="inline-block min-w-max px-4 text-sm font-black uppercase tracking-widest"
              style={{ color: i % 2 === 0 ? PINK : CYAN }}
            >
              {marqueeText}
            </span>
          ))}
        </div>
      </div>

      <GroveLaunchWaitlistModal open={openWaitlist} onOpenChange={setOpenWaitlist} initialFull={isFull} />
      <GroveLaunchTermsModal open={openTerms} onOpenChange={setOpenTerms} />
    </>
  );
};

export default HeroSection;
